import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  Divider,
  Alert,
  IconButton,
  TextField,
  Snackbar,
  Tooltip,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material'
import { TestResult, Test } from '../../types/test'
import { QuestionReportService } from '../../services/questionReportService'

interface TestResultModalProps {
  open: boolean
  onClose: () => void
  result: TestResult | null
  testId: string
  test?: Test | null
  onRetake?: () => void
}

export const TestResultModal: React.FC<TestResultModalProps> = ({
  open,
  onClose,
  result,
  testId,
  test,
  onRetake,
}) => {
  const { t } = useTranslation('test')

  // 報告ダイアログの状態
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportQuestionId, setReportQuestionId] = useState<string | null>(null)
  const [reportQuestionNumber, setReportQuestionNumber] = useState<number>(0)
  const [reportQuestionText, setReportQuestionText] = useState<string>('')
  const [reportQuestionOptions, setReportQuestionOptions] = useState<string[]>([])
  const [reportComment, setReportComment] = useState('')
  const [reportLoading, setReportLoading] = useState(false)
  const [reportedQuestions, setReportedQuestions] = useState<Set<string>>(new Set())

  // Snackbar の状態
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  if (!result) return null

  const handleRetake = () => {
    if (onRetake) {
      onRetake()
    }
    onClose()
  }

  const handleOpenReportDialog = (questionId: string, questionNumber: number) => {
    // テストデータから設問テキストと選択肢を取得
    const question = test?.questions.find((q) => q.id === questionId)
    setReportQuestionId(questionId)
    setReportQuestionNumber(questionNumber)
    setReportQuestionText(question?.questionText || '')
    setReportQuestionOptions(question?.options || [])
    setReportComment('')
    setReportDialogOpen(true)
  }

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false)
    setReportQuestionId(null)
    setReportQuestionNumber(0)
    setReportQuestionText('')
    setReportQuestionOptions([])
    setReportComment('')
  }

  const handleSubmitReport = async () => {
    if (!reportQuestionId) return

    setReportLoading(true)
    try {
      await QuestionReportService.createReport({
        questionId: reportQuestionId,
        testId,
        comment: reportComment || undefined,
      })

      setReportedQuestions((prev) => new Set(prev).add(reportQuestionId))
      setSnackbar({
        open: true,
        message: t('result.report.success'),
        severity: 'success',
      })
      handleCloseReportDialog()
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
            t('result.report.error')
          : t('result.report.error')

      if (errorMessage.includes('通報済み') || errorMessage.includes('already reported')) {
        setReportedQuestions((prev) => new Set(prev).add(reportQuestionId))
        setSnackbar({
          open: true,
          message: t('result.report.alreadyReported'),
          severity: 'error',
        })
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        })
      }
    } finally {
      setReportLoading(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {result.passed ? (
              <CheckCircleIcon color="primary" fontSize="large" />
            ) : (
              <CancelIcon sx={{ color: 'text.secondary' }} fontSize="large" />
            )}
            <Box>
              <Typography variant="h5" component="h2">
                {t('result.title')}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {t('result.attemptCount', { count: result.attempt })}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* スコア表示 */}
          <Box textAlign="center" mb={3}>
            <Typography
              variant="h2"
              component="div"
              color={result.passed ? 'primary.main' : 'text.secondary'}
            >
              {t('result.scorePoints', { score: result.score })}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('result.passingScorePoints', { score: result.passingScore })}
            </Typography>
            <Chip
              label={result.passed ? t('result.passed') : t('result.failed')}
              color={result.passed ? 'success' : 'error'}
              size="medium"
              sx={{ mt: 1 }}
            />
          </Box>

          {/* 結果メッセージ */}
          {result.passed ? (
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('result.passMessage')}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {t('result.failMessage')}
            </Alert>
          )}

          {/* 問題別結果 */}
          <Typography variant="h6" gutterBottom>
            {t('result.questionResults')}
          </Typography>
          <List>
            {result.detailedResults.map((questionResult, index) => (
              <React.Fragment key={questionResult.questionId}>
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Box display="flex" alignItems="center" width="100%" mb={1}>
                    <Typography variant="subtitle2" sx={{ mr: 2 }}>
                      {t('result.questionNumber', { number: index + 1 })}
                    </Typography>
                    <Chip
                      icon={questionResult.isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
                      label={questionResult.isCorrect ? t('result.correct') : t('result.incorrect')}
                      color={questionResult.isCorrect ? 'success' : 'error'}
                      size="small"
                    />
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption">
                        {t('result.pointsEarned', {
                          earned: questionResult.isCorrect ? questionResult.points : 0,
                          total: questionResult.points,
                        })}
                      </Typography>
                      <Tooltip
                        title={
                          reportedQuestions.has(questionResult.questionId)
                            ? t('result.report.alreadyReported')
                            : t('result.report.button')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenReportDialog(questionResult.questionId, index + 1)
                            }
                            disabled={reportedQuestions.has(questionResult.questionId)}
                            sx={{
                              color: reportedQuestions.has(questionResult.questionId)
                                ? 'text.disabled'
                                : 'text.secondary',
                              '&:hover': {
                                bgcolor: 'grey.100',
                              },
                            }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  {questionResult.explanation && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      <strong>{t('result.explanation')}:</strong> {questionResult.explanation}
                    </Typography>
                  )}
                </ListItem>
                {index < result.detailedResults.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('result.close')}</Button>
          {result.canRetake && !result.passed && (
            <Button
              onClick={handleRetake}
              variant="contained"
              startIcon={<RefreshIcon />}
              color="primary"
            >
              {t('result.retake')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 報告ダイアログ */}
      <Dialog open={reportDialogOpen} onClose={handleCloseReportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('result.report.dialogTitle')}</DialogTitle>
        <DialogContent>
          {/* 設問情報 */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.primary" gutterBottom>
              {t('result.questionNumber', { number: reportQuestionNumber })}
            </Typography>
            {reportQuestionText && (
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ mb: reportQuestionOptions.length > 0 ? 1.5 : 0 }}
              >
                {reportQuestionText}
              </Typography>
            )}
            {reportQuestionOptions.length > 0 && (
              <Box sx={{ pl: 1 }}>
                {reportQuestionOptions.map((option, idx) => (
                  <Typography key={idx} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {String.fromCharCode(65 + idx)}. {option}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('result.report.dialogDescription')}
          </Typography>
          <TextField
            label={t('result.report.commentLabel')}
            placeholder={t('result.report.commentPlaceholder')}
            multiline
            rows={4}
            fullWidth
            value={reportComment}
            onChange={(e) => setReportComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog} disabled={reportLoading}>
            {t('result.report.cancel')}
          </Button>
          <Button
            onClick={handleSubmitReport}
            variant="contained"
            color="primary"
            disabled={reportLoading}
          >
            {t('result.report.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
